import { apiClient } from "@config/apiClient";
import {
  IMeetingUpdatePayload,
  IMeetingObject,
  IMeetingResponse,
  IMeetingAttendeePayload,
} from "@interfaces/meeting.interface";
import { CreateMeetingObjectRequest } from "@app-types/requests.type";
import { IMeetingAttendeeResponse } from "@interfaces/responses.interface";
import { API_URL } from "@config/constants";

class MeetingService {
  private URL = `${API_URL}/meetings`;

  async getMeeting(itemId: string, calendarId?: string | null): Promise<IMeetingResponse> {
    const params = new URLSearchParams({ identifier: itemId });

    if (calendarId) {
      params.append("calendar_id", calendarId);
    }

    return apiClient.get(`${this.URL}/add-in/get-meeting/?${params.toString()}`);
  }

  async syncMeeting(itemId: string, calendarId?: string | null): Promise<IMeetingResponse> {
    try {
      const body: { identifier: string; calendar_id?: string } = { identifier: itemId };

      // Add calendar id to the body if it is provided.
      if (calendarId) {
        body.calendar_id = calendarId;
      }

      return await apiClient.post(`${this.URL}/add-in/sync-meeting/`, body);
    } catch (error: any) {
      if (error?.response?.status === 400) {
        const err = new Error(`Failed to sync meeting for identifier: ${itemId}`);
        (err as any).response = error.response;
        throw err;
      }
      throw error;
    }
  }

  async updateMeeting(
    meetingId: string,
    data: Partial<IMeetingUpdatePayload>
  ): Promise<IMeetingResponse> {
    return apiClient.patch(`${this.URL}/meetings/${meetingId}/`, data);
  }

  async removeMeeting(
    meetingId: string,
    remove_all_objects_except: string[]
  ): Promise<IMeetingResponse> {
    return apiClient.delete(`${this.URL}/add-in/${meetingId}/`, {
      data: { remove_all_objects_except },
    });
  }

  async addMeetingAttendee(
    meetingId: string,
    data: IMeetingAttendeePayload
  ): Promise<IMeetingAttendeeResponse> {
    return apiClient.post(`${this.URL}/meetings/${meetingId}/add-attendee/`, data);
  }

  async removeMeetingAttendee(
    meetingId: string,
    data: IMeetingAttendeePayload
  ): Promise<IMeetingAttendeeResponse> {
    return apiClient.post(`${this.URL}/meetings/${meetingId}/remove-attendee/`, data);
  }

  async getMeetingObjects(meetingId: string): Promise<IMeetingObject[]> {
    return apiClient.get(`${this.URL}/add-in/${meetingId}/meeting-objects/`);
  }

  async createMeetingObject(
    meetingId: string,
    data: CreateMeetingObjectRequest
  ): Promise<IMeetingObject> {
    return apiClient.post(`${this.URL}/add-in/${meetingId}/meeting-objects/`, data);
  }
}

const meetingService = new MeetingService();
export default meetingService;
